import { Link as RouterLink } from 'react-router-dom';
import { useDisplayPreferences } from '@/shared/hooks/useDisplayPreferences';
import { fontFamilies, r, s } from '@/theme/tokens';
import { Chip, Inline, Text } from '@/ui';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, Link as MuiLink, Tooltip } from '@/ui/primitives';
import { linkTruncateSx } from '../cellContents/shared';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '../tableTooltip';
import { OverflowTooltip } from './OverflowTooltip';
import {
  tableCellContentBoxSx,
  tableCellPrimaryTextSx,
  tableCellSecondaryTextSx,
  tableCellTagChipSx,
  tableCellTagRowSx,
  tableCellTextBlockSx,
} from './sharedCellContent';
import type { TableTextLine } from '../tableTypes';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type TableTextCellProps = { primary: TableTextLine; secondary?: TableTextLine[] };
type TableNameCellProps = {
  primary: TableTextLine;
  secondary?: TableTextLine | null;
  tags?: Array<{ label: string; tone: React.ComponentProps<typeof Chip>['tone'] }>;
};
type RunLinkCellProps = {
  label: string;
  href?: string | null;
  latest?: boolean;
  onClick?: () => void;
  align?: 'left' | 'right';
};
type RelativeTimeTooltipCellProps = {
  value: Date | string | null | undefined;
  emptyLabel?: string;
};

const numericTextSx = {
  fontSize: '13px',
  lineHeight: 1.2,
};

const toSxProps = (value: SxProps<Theme> | undefined): SxProps<Theme> => value ?? [];

const mergeSxProps = (...values: Array<SxProps<Theme> | undefined>): SxProps<Theme> => {
  const resolvedValues: Array<SxProps<Theme>> = [];

  values.forEach((value) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      resolvedValues.push(...(value as Array<SxProps<Theme>>));
      return;
    }

    resolvedValues.push(value);
  });

  return resolvedValues as SxProps<Theme>;
};

const buildLiteralSx = (
  prefersMonospaceLiterals: boolean,
  literal: boolean | undefined,
): SxProps<Theme> =>
  prefersMonospaceLiterals && literal ? [{ fontFamily: fontFamilies.mono }] : [];

const extractTooltipFontSx = (styles: SxProps<Theme>): SxProps<Theme> => {
  const resolvedStyles = Array.isArray(styles) ? styles : [styles];
  const fontStyles = resolvedStyles.filter(
    (style) =>
      !Array.isArray(style) && style !== null && typeof style === 'object' && 'fontFamily' in style,
  );

  return fontStyles as SxProps<Theme>;
};

export const TableTextCell = ({ primary, secondary }: TableTextCellProps): ReactElement => {
  const { prefersMonospaceLiterals } = useDisplayPreferences();
  const secondaryLines = secondary ?? [];
  const primarySx = toSxProps(primary.sx);
  const primaryRole = primary.role ?? 'body';
  const primaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, primary.literal);
  const primaryTooltipSx = extractTooltipFontSx(mergeSxProps(primaryLiteralSx, primarySx));
  const primaryHref = primary.href;
  return (
    <Box sx={mergeSxProps(tableCellTextBlockSx, { rowGap: '8px' })}>
      {primaryHref ? (
        <Text role={primaryRole} sx={{ width: '100%', minWidth: 0 }}>
          <OverflowTooltip title={primary.label} tooltipSx={primaryTooltipSx}>
            <MuiLink
              component={RouterLink}
              to={primaryHref}
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                primary.onClick?.();
              }}
              sx={mergeSxProps(linkTruncateSx, primaryLiteralSx, primarySx)}
            >
              {primary.label}
            </MuiLink>
          </OverflowTooltip>
        </Text>
      ) : (
        <OverflowTooltip title={primary.label} tooltipSx={primaryTooltipSx}>
          <Text
            role={primaryRole}
            truncate
            sx={mergeSxProps(
              { display: 'block', width: '100%', minWidth: 0, maxWidth: '100%' },
              primaryLiteralSx,
              primarySx,
            )}
          >
            {primary.label}
          </Text>
        </OverflowTooltip>
      )}
      {secondaryLines.map((line, index) => {
        const lineRole = line.role ?? 'body';
        const lineLiteralSx = buildLiteralSx(prefersMonospaceLiterals, line.literal);
        const lineSx = toSxProps(line.sx);
        const lineTooltipSx = extractTooltipFontSx(mergeSxProps(lineLiteralSx, lineSx));
        return (
          <Text
            key={`${line.href ?? 'static'}-${line.label}-${String(index)}`}
            role={lineRole}
            color="textSecondary"
            truncate={!line.href}
            sx={mergeSxProps({ width: '100%', minWidth: 0 }, lineLiteralSx, lineSx)}
          >
            {line.href ? (
              <OverflowTooltip title={line.label} tooltipSx={lineTooltipSx}>
                <MuiLink
                  component={RouterLink}
                  to={line.href}
                  underline="hover"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    line.onClick?.();
                  }}
                  sx={mergeSxProps(linkTruncateSx, lineLiteralSx, lineSx)}
                >
                  {line.label}
                </MuiLink>
              </OverflowTooltip>
            ) : (
              <OverflowTooltip title={line.label} tooltipSx={lineTooltipSx}>
                <span>{line.label}</span>
              </OverflowTooltip>
            )}
          </Text>
        );
      })}
    </Box>
  );
};

export const TableNameCell = ({
  primary,
  secondary = null,
  tags = [],
}: TableNameCellProps): ReactElement => {
  const { prefersMonospaceLiterals } = useDisplayPreferences();
  const primaryRole = primary.role ?? 'body';
  const primarySx = toSxProps(primary.sx);
  const primaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, primary.literal);
  const primaryTooltipSx = extractTooltipFontSx(mergeSxProps(primaryLiteralSx, primarySx));
  const secondaryRole = secondary?.role ?? 'body';
  const secondarySx = toSxProps(secondary?.sx);
  const secondaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, secondary?.literal);
  const secondaryTooltipSx = extractTooltipFontSx(mergeSxProps(secondaryLiteralSx, secondarySx));

  return (
    <Box sx={tableCellTextBlockSx}>
      {primary.href ? (
        <Text role={primaryRole} dataGridTypography="custom" sx={tableCellPrimaryTextSx}>
          <OverflowTooltip title={primary.label} tooltipSx={primaryTooltipSx}>
            <MuiLink
              component={RouterLink}
              to={primary.href}
              data-grid-typography="custom"
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                primary.onClick?.();
              }}
              sx={mergeSxProps(linkTruncateSx, primaryLiteralSx, primarySx)}
            >
              {primary.label}
            </MuiLink>
          </OverflowTooltip>
        </Text>
      ) : (
        <OverflowTooltip title={primary.label} tooltipSx={primaryTooltipSx}>
          <Text
            role={primaryRole}
            dataGridTypography="custom"
            truncate
            sx={mergeSxProps(
              tableCellPrimaryTextSx,
              { display: 'block' },
              primaryLiteralSx,
              primarySx,
            )}
          >
            {primary.label}
          </Text>
        </OverflowTooltip>
      )}
      {secondary ? (
        secondary.href ? (
          <Text
            role={secondaryRole}
            dataGridTypography="custom"
            color="textSecondary"
            sx={tableCellSecondaryTextSx}
          >
            <OverflowTooltip title={secondary.label} tooltipSx={secondaryTooltipSx}>
              <MuiLink
                component={RouterLink}
                to={secondary.href}
                data-grid-typography="custom"
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                  secondary.onClick?.();
                }}
                sx={mergeSxProps(linkTruncateSx, secondaryLiteralSx, secondarySx)}
              >
                {secondary.label}
              </MuiLink>
            </OverflowTooltip>
          </Text>
        ) : (
          <OverflowTooltip title={secondary.label} tooltipSx={secondaryTooltipSx}>
            <Text
              role={secondaryRole}
              dataGridTypography="custom"
              color="textSecondary"
              truncate
              sx={mergeSxProps(
                tableCellSecondaryTextSx,
                { display: 'block' },
                secondaryLiteralSx,
                secondarySx,
              )}
            >
              {secondary.label}
            </Text>
          </OverflowTooltip>
        )
      ) : null}
      {tags.length > 0 ? (
        <Box sx={[tableCellTagRowSx, { mt: s('sm') }]}>
          <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
            {tags.map((tag, index) => (
              <Chip
                key={`${tag.label}-${String(index)}`}
                size="xs"
                tone={tag.tone}
                label={tag.label}
                sx={tableCellTagChipSx}
              />
            ))}
          </Inline>
        </Box>
      ) : null}
    </Box>
  );
};

export const RunLinkCell = ({
  label,
  href,
  latest = false,
  onClick,
  align = 'left',
}: RunLinkCellProps): ReactElement => {
  const runNode = href ? (
    <OverflowTooltip title={label} inline>
      <MuiLink
        component={RouterLink}
        to={href}
        underline="hover"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        sx={mergeSxProps(linkTruncateSx, numericTextSx)}
      >
        {label}
      </MuiLink>
    </OverflowTooltip>
  ) : (
    <OverflowTooltip title={label} inline>
      <Text role="body" component="span" sx={mergeSxProps(linkTruncateSx, numericTextSx)}>
        {label}
      </Text>
    </OverflowTooltip>
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', mt: s('xs') }}>
      <Inline
        gap="md"
        align="center"
        sx={{
          width: '100%',
          minWidth: 0,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
      >
        <Text role="body" component="span" sx={{ minWidth: 0, ...numericTextSx }}>
          {runNode}
        </Text>
        {latest ? (
          <Chip
            label="Latest"
            tone="primary"
            size="xs"
            sx={{
              borderRadius: r('pill'),
              '& .MuiChip-label .MuiTypography-root': {
                fontSize: '13px',
                lineHeight: 1.2,
              },
            }}
          />
        ) : null}
      </Inline>
    </Box>
  );
};

export const RelativeTimeTooltipCell = ({
  value,
  emptyLabel = '-',
}: RelativeTimeTooltipCellProps): ReactElement => {
  const timestamp = value ?? null;
  const label = timestamp ? formatRelativeTime(timestamp) : emptyLabel;
  const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
  return (
    <Tooltip
      title={fullTimestamp}
      disableHoverListener={!fullTimestamp}
      enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      arrow
      slotProps={{
        tooltip: { sx: { fontSize: '15px', lineHeight: 1.4 } },
      }}
    >
      <Box component="span" sx={tableCellContentBoxSx}>
        <Text role="body" component="span" sx={numericTextSx}>
          {label.length > 0 ? label : emptyLabel}
        </Text>
      </Box>
    </Tooltip>
  );
};
